import { useMemo } from "react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar.tsx";
import { GroupChannel } from "@sendbird/chat/groupChannel";
import { cn, getInitials } from "@/lib/utils.ts";
import { useSignedMedia } from "@/lib/hooks/use-signed-media.ts";

export function useChannelAvatar(
  channel: GroupChannel | undefined,
  userId: string,
  priority: boolean,
  isOnline: boolean,
  isNewTeen: boolean,
) {
  const otherMember = useMemo(() => {
    if (!channel) return null;

    const { members } = channel;
    const membersExcludingMe = members.filter(
      (member) => member.userId !== userId,
    );

    // The other user in the conversation
    const [firstMember] = membersExcludingMe;

    return firstMember;
  }, [channel, userId]);

  const { url: profileUrl } = useSignedMedia(otherMember?.profileUrl);

  return useMemo(() => {
    if (!channel) return null;

    return (
      <div className="relative">
        <div
          className={cn([
            "flex h-11 w-11 items-center justify-center rounded-full",
            priority ? "bg-red-500" : "bg-transparent",
            isNewTeen ? "bg-[#138A00]" : "bg-transparent",
          ])}
        >
          <Avatar
            className={cn({
              "border-2 border-white": priority || isNewTeen,
            })}
          >
            <AvatarImage src={profileUrl} />
            <AvatarFallback>
              {getInitials(otherMember?.nickname || "U")}
            </AvatarFallback>
          </Avatar>
        </div>
        {isOnline && (
          <div
            className={cn(
              "absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-lime-700",
            )}
          />
        )}
      </div>
    );
  }, [userId, channel, isOnline, priority, otherMember, profileUrl]);
}
